import React, { useState, useRef } from 'react';
import { MacWindow } from './MacWindow';
import { Download, Sparkles, Copy, Check, Palette, Layers, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../../store/ToastContext';

interface LogoShowcaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LogoShowcaseModal: React.FC<LogoShowcaseModalProps> = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [selectedVariant, setSelectedVariant] = useState<'hologram' | 'gold' | 'neon' | 'flat'>('hologram');
  const [copiedSvg, setCopiedSvg] = useState(false);
  const svgPreviewRef = useRef<SVGSVGElement>(null);

  if (!isOpen) return null;

  const getGradientDefs = (variant: 'hologram' | 'gold' | 'neon' | 'flat') => {
    switch (variant) {
      case 'gold':
        return (
          <>
            <linearGradient id="showcase-bg" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#d97706" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#b45309" />
            </linearGradient>
            <linearGradient id="showcase-foil" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#fef08a" stopOpacity="0.85" />
              <stop offset="50%" stopColor="#ca8a04" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.9" />
            </linearGradient>
          </>
        );
      case 'neon':
        return (
          <>
            <linearGradient id="showcase-bg" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="50%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
            <linearGradient id="showcase-foil" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#a855f7" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.9" />
            </linearGradient>
          </>
        );
      case 'flat':
        return (
          <>
            <linearGradient id="showcase-bg" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#1d4ed8" />
            </linearGradient>
            <linearGradient id="showcase-foil" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.1" />
            </linearGradient>
          </>
        );
      default: // Hologram
        return (
          <>
            <linearGradient id="showcase-bg" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="45%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
            <linearGradient id="showcase-foil" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.75" />
              <stop offset="35%" stopColor="#ec4899" stopOpacity="0.75" />
              <stop offset="70%" stopColor="#eab308" stopOpacity="0.75" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.75" />
            </linearGradient>
          </>
        );
    }
  };

  const generateSvgString = () => {
    return `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="vdp-logo-bg" x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#3b82f6" />
      <stop offset="45%" stop-color="#6366f1" />
      <stop offset="100%" stop-color="#a855f7" />
    </linearGradient>
    <linearGradient id="vdp-peel-grad" x1="60" y1="10" x2="90" y2="40" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#f8fafc" />
      <stop offset="40%" stop-color="#e2e8f0" />
      <stop offset="100%" stop-color="#94a3b8" />
    </linearGradient>
    <linearGradient id="vdp-foil" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.8" />
      <stop offset="35%" stop-color="#ec4899" stop-opacity="0.8" />
      <stop offset="70%" stop-color="#eab308" stop-opacity="0.8" />
      <stop offset="100%" stop-color="#10b981" stop-opacity="0.8" />
    </linearGradient>
    <filter id="vdp-peel-shadow" x="50" y="0" width="50" height="50" filterUnits="userSpaceOnUse">
      <feDropShadow dx="-2" dy="3" stdDeviation="3" flood-color="#09090b" flood-opacity="0.4" />
    </filter>
  </defs>
  <path d="M 18 10 L 65 10 L 90 35 L 90 82 A 8 8 0 0 1 82 90 L 18 90 A 8 8 0 0 1 10 82 L 10 18 A 8 8 0 0 1 18 10 Z" fill="url(#vdp-logo-bg)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
  <path d="M 18 10 L 65 10 L 90 35 L 90 82 A 8 8 0 0 1 82 90 L 18 90 A 8 8 0 0 1 10 82 L 10 18 A 8 8 0 0 1 18 10 Z" fill="url(#vdp-foil)" opacity="0.22"/>
  <line x1="22" y1="36" x2="52" y2="36" stroke="#ffffff" stroke-width="3" stroke-linecap="round" opacity="0.9"/>
  <line x1="22" y1="46" x2="62" y2="46" stroke="#ffffff" stroke-width="3" stroke-linecap="round" opacity="0.9"/>
  <line x1="22" y1="56" x2="44" y2="56" stroke="#ffffff" stroke-width="3" stroke-linecap="round" opacity="0.9"/>
  <rect x="22" y="66" width="3" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="28" y="66" width="6" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="37" y="66" width="2" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="42" y="66" width="5" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="50" y="66" width="3" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="56" y="66" width="7" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="66" y="66" width="4" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <rect x="73" y="66" width="2" height="14" rx="1" fill="#ffffff" opacity="0.95"/>
  <path d="M 72 40 Q 72 48 80 48 Q 72 48 72 56 Q 72 48 64 48 Q 72 48 72 40 Z" fill="#ffffff" opacity="0.95"/>
  <circle cx="72" cy="48" r="1.5" fill="#fef08a"/>
  <path d="M 65 10 L 90 35 L 65 35 Z" fill="#09090b" opacity="0.25"/>
  <path d="M 65 10 L 90 35 L 65 35 Z" fill="url(#vdp-peel-grad)" filter="url(#vdp-peel-shadow)"/>
  <line x1="65" y1="10" x2="65" y2="35" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
  <line x1="65" y1="35" x2="90" y2="35" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
</svg>`;
  };

  const handleDownloadSvg = () => {
    const svgStr = generateSvgString();
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `variable_sticker_logo_${selectedVariant}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Vector SVG Downloaded', `Saved variable_sticker_logo_${selectedVariant}.svg`);
  };

  const handleDownloadPng = () => {
    const svgStr = generateSvgString();
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      ctx.clearRect(0, 0, 1024, 1024);
      ctx.drawImage(img, 0, 0, 1024, 1024);
      URL.revokeObjectURL(url);

      canvas.toBlob((pngBlob) => {
        if (!pngBlob) return;
        const pngUrl = URL.createObjectURL(pngBlob);
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `variable_sticker_logo_${selectedVariant}_1024px.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(pngUrl);
        toast.success('High-Res PNG Downloaded', `Saved 1024x1024 PNG logo`);
      }, 'image/png');
    };
    img.src = url;
  };

  const handleCopySvg = () => {
    const svgStr = generateSvgString();
    navigator.clipboard.writeText(svgStr);
    setCopiedSvg(true);
    setTimeout(() => setCopiedSvg(false), 2000);
    toast.success('SVG Code Copied', 'Vector code copied to clipboard');
  };

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Variable Sticker Studio - Official Brand Logo" maxWidth="max-w-2xl">
      <div className="p-6 flex flex-col gap-6">
        
        {/* Header Intro */}
        <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-purple-600 flex items-center justify-center text-white shadow-md">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base text-zinc-100">App Identity &amp; Logo System</h3>
              <p className="text-xs text-zinc-400">Custom crafted vector emblem with holographic peel dynamics &amp; variable data lines</p>
            </div>
          </div>
        </div>

        {/* Logo Preview Stage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          
          {/* Main 3D Emblem Stage */}
          <div className="flex flex-col items-center justify-center p-8 bg-zinc-950 rounded-2xl border border-zinc-800 relative overflow-hidden shadow-inner group">
            <div className="absolute inset-0 bg-radial from-blue-500/10 via-transparent to-transparent opacity-50 pointer-events-none" />
            
            <div className="w-40 h-40 relative z-10 transition-transform duration-300 group-hover:scale-105">
              <svg
                ref={svgPreviewRef}
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full drop-shadow-2xl"
              >
                <defs>
                  {getGradientDefs(selectedVariant)}
                  <linearGradient id="showcase-peel-grad" x1="60" y1="10" x2="90" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#f8fafc" />
                    <stop offset="40%" stopColor="#e2e8f0" />
                    <stop offset="100%" stopColor="#94a3b8" />
                  </linearGradient>
                  <filter id="showcase-peel-shadow" x="50" y="0" width="50" height="50" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="-2" dy="3" stdDeviation="3" floodColor="#09090b" floodOpacity="0.4" />
                  </filter>
                </defs>

                <path
                  d="M 18 10 L 65 10 L 90 35 L 90 82 A 8 8 0 0 1 82 90 L 18 90 A 8 8 0 0 1 10 82 L 10 18 A 8 8 0 0 1 18 10 Z"
                  fill="url(#showcase-bg)"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="2"
                />

                <path
                  d="M 18 10 L 65 10 L 90 35 L 90 82 A 8 8 0 0 1 82 90 L 18 90 A 8 8 0 0 1 10 82 L 10 18 A 8 8 0 0 1 18 10 Z"
                  fill="url(#showcase-foil)"
                  opacity="0.25"
                />

                <line x1="22" y1="36" x2="52" y2="36" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" opacity="0.9" />
                <line x1="22" y1="46" x2="62" y2="46" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" opacity="0.9" />
                <line x1="22" y1="56" x2="44" y2="56" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" opacity="0.9" />

                <rect x="22" y="66" width="3" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="28" y="66" width="6" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="37" y="66" width="2" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="42" y="66" width="5" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="50" y="66" width="3" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="56" y="66" width="7" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="66" y="66" width="4" height="14" rx="1" fill="#ffffff" opacity="0.95" />
                <rect x="73" y="66" width="2" height="14" rx="1" fill="#ffffff" opacity="0.95" />

                <path
                  d="M 72 40 Q 72 48 80 48 Q 72 48 72 56 Q 72 48 64 48 Q 72 48 72 40 Z"
                  fill="#ffffff"
                  opacity="0.95"
                />
                <circle cx="72" cy="48" r="1.5" fill="#fef08a" />

                <path d="M 65 10 L 90 35 L 65 35 Z" fill="#09090b" opacity="0.25" />
                <path d="M 65 10 L 90 35 L 65 35 Z" fill="url(#showcase-peel-grad)" filter="url(#showcase-peel-shadow)" />
                <line x1="65" y1="10" x2="65" y2="35" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
                <line x1="65" y1="35" x2="90" y2="35" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
              </svg>
            </div>

            <div className="mt-4 text-center">
              <div className="font-extrabold text-sm text-zinc-100">Variable Sticker</div>
              <div className="text-[10px] text-zinc-400 font-mono">PrintScale VDP Studio</div>
            </div>
          </div>

          {/* Controls & Variant Selection */}
          <div className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-semibold text-zinc-300 mb-2 block flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5 text-blue-400" />
                Select Finish &amp; Color Style:
              </label>
              
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'hologram', label: 'Holo Iridescent', desc: 'Blue/Indigo Gradient' },
                  { id: 'gold', label: '24K Liquid Gold', desc: 'Luxury Gold Foil' },
                  { id: 'neon', label: 'Cyberpunk Neon', desc: 'Cyan / Magenta Glow' },
                  { id: 'flat', label: 'Classic Pro Blue', desc: 'Minimal Pure Vector' }
                ].map(v => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVariant(v.id as any)}
                    className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                      selectedVariant === v.id
                        ? 'bg-blue-600/20 border-blue-500 text-blue-300 shadow-sm'
                        : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    <div className="font-bold text-xs">{v.label}</div>
                    <div className="text-[10px] opacity-75">{v.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Export Actions */}
            <div className="pt-2 border-t border-zinc-800 flex flex-col gap-2">
              <button
                onClick={handleDownloadSvg}
                className="w-full py-2 px-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <Download className="w-4 h-4" /> Download Scalable Vector (.SVG)
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadPng}
                  className="flex-1 py-2 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <ImageIcon className="w-3.5 h-3.5 text-purple-400" /> Download 1024px PNG
                </button>
                <button
                  onClick={handleCopySvg}
                  className="py-2 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  title="Copy SVG XML"
                >
                  {copiedSvg ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedSvg ? 'Copied' : 'Copy Code'}</span>
                </button>
              </div>
            </div>

          </div>

        </div>

      </div>
    </MacWindow>
  );
};
